import { getCloseMatches } from 'difflib'
import { Validator } from 'jsonschema'
import { parse, YAMLParseError } from 'yaml'

const IntegrationSchema = {
  description: 'Base config model.',
  properties: {
    name: {
      title: 'Name',
      type: 'string',
    },
    logo: {
      title: 'Logo',
      type: 'string',
    },
    docs: {
      title: 'Docs',
      type: 'string',
    },
    unique_key: {
      title: 'Unique Key',
      type: 'string',
    },
    categories: {
      items: {
        type: 'string',
      },
      title: 'Categories',
      type: 'array',
    },
    description: {
      title: 'Description',
      type: 'string',
    },
    documentation_doc_text: {
      anyOf: [
        {
          type: 'string',
        },
        {
          type: 'null',
        },
      ],
      default: null,
      title: 'Documentation Doc Text',
    },
    configuration_docs_text: {
      anyOf: [
        {
          type: 'string',
        },
        {
          type: 'null',
        },
      ],
      default: null,
      title: 'Configuration Docs Text',
    },
    callback_url: {
      anyOf: [
        {
          type: 'string',
        },
        {
          type: 'null',
        },
      ],
      default: null,
      title: 'Callback Url',
    },
    get_current_user_endpoint: {
      anyOf: [
        {
          type: 'string',
        },
        {
          type: 'null',
        },
      ],
      default: null,
      title: 'Get Current User Endpoint',
    },
    status: {
      anyOf: [
        {
          type: 'string',
        },
        {
          type: 'null',
        },
      ],
      default: null,
      title: 'Status',
    },
    no_auth: {
      default: false,
      title: 'No Auth',
      type: 'boolean',
    },
    auth_schemes: {
      type: 'array',
      title: 'AuthSchemes',
    },
    extends: {
      anyOf: [
        {
          type: 'string',
        },
        {
          type: 'null',
        },
      ],
      default: null,
      title: 'Extends',
    },
    run_in_sandbox: {
      default: false,
      title: 'Run In Sandbox',
      type: 'boolean',
    },
  },
  required: [
    'name',
    'logo',
    'unique_key',
    'categories',
    'description',
    'auth_schemes',
  ],
  title: 'AppConfig',
  type: 'object',
}

const BasicAuthScheme = {
  $defs: {
    AuthSchemaField: {
      description: 'Auth field schema.',
      properties: {
        name: {
          title: 'Name',
          type: 'string',
        },
        type: {
          title: 'Type',
          type: 'string',
        },
        description: {
          title: 'Description',
          type: 'string',
        },
        display_name: {
          title: 'Display Name',
          type: 'string',
        },
        default: {
          anyOf: [
            {
              type: 'number',
            },
            {
              type: 'integer',
            },
            {
              type: 'string',
            },
            {
              type: 'boolean',
            },
            {
              items: {},
              type: 'array',
            },
            {
              type: 'object',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Default',
        },
        required: {
          default: false,
          title: 'Required',
          type: 'boolean',
        },
        get_current_user_endpoint: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Get Current User Endpoint',
        },
        expected_from_customer: {
          default: false,
          title: 'Expected From Customer',
          type: 'boolean',
        },
        is_secret: {
          default: false,
          title: 'Is Secret',
          type: 'boolean',
        },
      },
      required: ['name', 'type', 'description', 'display_name'],
      title: 'AuthSchemaField',
      type: 'object',
    },
  },
  description: 'Basic auth scheme.',
  properties: {
    name: {
      title: 'Name',
      type: 'string',
    },
    proxy: {
      title: 'Proxy',
      type: 'object',
    },
    mode: {
      default: 'BASIC',
      title: 'Mode',
      type: 'string',
    },
    fields: {
      items: {
        $ref: '#/$defs/AuthSchemaField',
      },
      title: 'Fields',
      type: 'array',
    },
  },
  required: ['name', 'proxy'],
  title: 'BasicAuthScheme',
  type: 'object',
}

const NoAuthScheme = {
  $defs: {
    AuthSchemaField: {
      description: 'Auth field schema.',
      properties: {
        name: {
          title: 'Name',
          type: 'string',
        },
        type: {
          title: 'Type',
          type: 'string',
        },
        description: {
          title: 'Description',
          type: 'string',
        },
        display_name: {
          title: 'Display Name',
          type: 'string',
        },
        default: {
          anyOf: [
            {
              type: 'number',
            },
            {
              type: 'integer',
            },
            {
              type: 'string',
            },
            {
              type: 'boolean',
            },
            {
              items: {},
              type: 'array',
            },
            {
              type: 'object',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Default',
        },
        required: {
          default: false,
          title: 'Required',
          type: 'boolean',
        },
        get_current_user_endpoint: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Get Current User Endpoint',
        },
        expected_from_customer: {
          default: false,
          title: 'Expected From Customer',
          type: 'boolean',
        },
        is_secret: {
          default: false,
          title: 'Is Secret',
          type: 'boolean',
        },
      },
      required: ['name', 'type', 'description', 'display_name'],
      title: 'AuthSchemaField',
      type: 'object',
    },
  },
  description: 'Basic auth scheme.',
  properties: {
    name: {
      title: 'Name',
      type: 'string',
    },
    proxy: {
      title: 'Proxy',
      type: 'object',
    },
    mode: {
      default: 'NO_AUTH',
      title: 'Mode',
      type: 'string',
    },
    fields: {
      items: {
        $ref: '#/$defs/AuthSchemaField',
      },
      title: 'Fields',
      type: 'array',
    },
    no_auth: {
      default: true,
      title: 'No Auth',
      type: 'boolean',
    },
  },
  required: ['name'],
  title: 'NoAuthScheme',
  type: 'object',
}

const ApiKeyAuthScheme = {
  $defs: {
    AuthSchemaField: {
      description: 'Auth field schema.',
      properties: {
        name: {
          title: 'Name',
          type: 'string',
        },
        type: {
          title: 'Type',
          type: 'string',
        },
        description: {
          title: 'Description',
          type: 'string',
        },
        display_name: {
          title: 'Display Name',
          type: 'string',
        },
        default: {
          anyOf: [
            {
              type: 'number',
            },
            {
              type: 'integer',
            },
            {
              type: 'string',
            },
            {
              type: 'boolean',
            },
            {
              items: {},
              type: 'array',
            },
            {
              type: 'object',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Default',
        },
        required: {
          default: false,
          title: 'Required',
          type: 'boolean',
        },
        get_current_user_endpoint: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Get Current User Endpoint',
        },
        expected_from_customer: {
          default: false,
          title: 'Expected From Customer',
          type: 'boolean',
        },
        is_secret: {
          default: false,
          title: 'Is Secret',
          type: 'boolean',
        },
      },
      required: ['name', 'type', 'description', 'display_name'],
      title: 'AuthSchemaField',
      type: 'object',
    },
  },
  properties: {
    name: {
      title: 'Name',
      type: 'string',
    },
    proxy: {
      anyOf: [
        {
          type: 'object',
        },
        {
          type: 'null',
        },
      ],
      default: null,
      title: 'Proxy',
    },
    mode: {
      default: 'API_KEY',
      title: 'Mode',
      type: 'string',
    },
    authorization_url: {
      anyOf: [
        {
          type: 'string',
        },
        {
          type: 'null',
        },
      ],
      default: null,
      title: 'Authorization Url',
    },
    token_url: {
      anyOf: [
        {
          type: 'string',
        },
        {
          type: 'null',
        },
      ],
      default: null,
      title: 'Token Url',
    },
    default_scopes: {
      anyOf: [
        {
          items: {
            type: 'string',
          },
          type: 'array',
        },
        {
          type: 'null',
        },
      ],
      default: null,
      title: 'Default Scopes',
    },
    authorization_params: {
      anyOf: [
        {
          type: 'object',
        },
        {
          type: 'null',
        },
      ],
      default: null,
      title: 'Authorization Params',
    },
    token_request_auth_method: {
      anyOf: [
        {
          type: 'string',
        },
        {
          type: 'null',
        },
      ],
      default: null,
      title: 'Token Request Auth Method',
    },
    token_params: {
      anyOf: [
        {
          type: 'object',
        },
        {
          type: 'null',
        },
      ],
      default: null,
      title: 'Token Params',
    },
    fields: {
      items: {
        $ref: '#/$defs/AuthSchemaField',
      },
      title: 'Fields',
      type: 'array',
    },
  },
  required: ['name'],
  title: 'ApiKeyAuthScheme',
  type: 'object',
}

const OAuth1AuthScheme = {
  $defs: {
    AuthSchemaField: {
      description: 'Auth field schema.',
      properties: {
        name: {
          title: 'Name',
          type: 'string',
        },
        type: {
          title: 'Type',
          type: 'string',
        },
        description: {
          title: 'Description',
          type: 'string',
        },
        display_name: {
          title: 'Display Name',
          type: 'string',
        },
        default: {
          anyOf: [
            {
              type: 'number',
            },
            {
              type: 'integer',
            },
            {
              type: 'string',
            },
            {
              type: 'boolean',
            },
            {
              items: {},
              type: 'array',
            },
            {
              type: 'object',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Default',
        },
        required: {
          default: false,
          title: 'Required',
          type: 'boolean',
        },
        get_current_user_endpoint: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Get Current User Endpoint',
        },
        expected_from_customer: {
          default: false,
          title: 'Expected From Customer',
          type: 'boolean',
        },
        is_secret: {
          default: false,
          title: 'Is Secret',
          type: 'boolean',
        },
      },
      required: ['name', 'type', 'description', 'display_name'],
      title: 'AuthSchemaField',
      type: 'object',
    },
  },
  properties: {
    name: {
      title: 'Name',
      type: 'string',
    },
    proxy: {
      title: 'Proxy',
      type: 'object',
    },
    mode: {
      default: 'OAUTH1',
      title: 'Mode',
      type: 'string',
    },
    token_url: {
      title: 'Token Url',
      type: 'string',
    },
    request_url: {
      title: 'Request Url',
      type: 'string',
    },
    authorization_url: {
      title: 'Authorization Url',
      type: 'string',
    },
    default_scopes: {
      items: {
        type: 'string',
      },
      title: 'Default Scopes',
      type: 'array',
    },
    scope_separator: {
      title: 'Scope Separator',
      type: 'string',
    },
    authorization_params: {
      title: 'Authorization Params',
      type: 'object',
    },
    signature_method: {
      title: 'Signature Method',
      type: 'string',
    },
    fields: {
      items: {
        $ref: '#/$defs/AuthSchemaField',
      },
      title: 'Fields',
      type: 'array',
    },
  },
  required: [
    'name',
    'proxy',
    'token_url',
    'request_url',
    'authorization_url',
    'default_scopes',
    'scope_separator',
    'authorization_params',
    'signature_method',
  ],
  title: 'OAuth1AuthScheme',
  type: 'object',
}

const OAuth2AuthScheme = {
  $defs: {
    AuthSchemaField: {
      description: 'Auth field schema.',
      properties: {
        name: {
          title: 'Name',
          type: 'string',
        },
        type: {
          title: 'Type',
          type: 'string',
        },
        description: {
          title: 'Description',
          type: 'string',
        },
        display_name: {
          title: 'Display Name',
          type: 'string',
        },
        default: {
          anyOf: [
            {
              type: 'number',
            },
            {
              type: 'integer',
            },
            {
              type: 'string',
            },
            {
              type: 'boolean',
            },
            {
              items: {},
              type: 'array',
            },
            {
              type: 'object',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Default',
        },
        required: {
          default: false,
          title: 'Required',
          type: 'boolean',
        },
        get_current_user_endpoint: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Get Current User Endpoint',
        },
        expected_from_customer: {
          default: false,
          title: 'Expected From Customer',
          type: 'boolean',
        },
        is_secret: {
          default: false,
          title: 'Is Secret',
          type: 'boolean',
        },
      },
      required: ['name', 'type', 'description', 'display_name'],
      title: 'AuthSchemaField',
      type: 'object',
    },
  },
  properties: {
    name: {
      title: 'Name',
      type: 'string',
    },
    proxy: {
      title: 'Proxy',
      type: 'object',
    },
    mode: {
      default: 'OAUTH2',
      title: 'Mode',
      type: 'string',
    },
    base_url: {
      anyOf: [
        {
          type: 'string',
        },
        {
          type: 'null',
        },
      ],
      default: null,
      title: 'Base Url',
    },
    token_url: {
      title: 'Token Url',
      type: 'string',
    },
    callback_url: {
      anyOf: [
        {
          type: 'string',
        },
        {
          type: 'null',
        },
      ],
      default: null,
      title: 'Callback Url',
    },
    authorization_url: {
      title: 'Authorization Url',
      type: 'string',
    },
    scope_separator: {
      anyOf: [
        {
          type: 'string',
        },
        {
          type: 'null',
        },
      ],
      default: null,
      title: 'Scope Separator',
    },
    default_scopes: {
      anyOf: [
        {
          items: {
            type: 'string',
          },
          type: 'array',
        },
        {
          type: 'null',
        },
      ],
      default: null,
      title: 'Default Scopes',
    },
    authorization_params: {
      anyOf: [
        {
          type: 'object',
        },
        {
          type: 'null',
        },
      ],
      default: null,
      title: 'Authorization Params',
    },
    token_access_request_method: {
      anyOf: [
        {
          type: 'string',
        },
        {
          type: 'null',
        },
      ],
      default: null,
      title: 'Token Access Request Method',
    },
    token_params: {
      anyOf: [
        {
          type: 'object',
        },
        {
          type: 'null',
        },
      ],
      default: null,
      title: 'Token Params',
    },
    fields: {
      items: {
        $ref: '#/$defs/AuthSchemaField',
      },
      title: 'Fields',
      type: 'array',
    },
    refresh_params: {
      anyOf: [
        {
          type: 'object',
        },
        {
          type: 'null',
        },
      ],
      default: null,
      title: 'Refresh Params',
    },
    token_response_metadata: {
      anyOf: [
        {
          items: {},
          type: 'array',
        },
        {
          type: 'null',
        },
      ],
      default: null,
      title: 'Token Response Metadata',
    },
    token_request_auth_method: {
      anyOf: [
        {
          type: 'string',
        },
        {
          type: 'null',
        },
      ],
      default: null,
      title: 'Token Request Auth Method',
    },
    disable_pkce: {
      default: false,
      title: 'Disable Pkce',
      type: 'boolean',
    },
    scope_seperator: {
      anyOf: [
        {
          type: 'string',
        },
        {
          type: 'null',
        },
      ],
      default: null,
      title: 'Scope Seperator',
    },
    available_scopes: {
      anyOf: [
        {
          items: {
            type: 'string',
          },
          type: 'array',
        },
        {
          type: 'null',
        },
      ],
      default: null,
      title: 'Available Scopes',
    },
    default_user_scopes: {
      anyOf: [
        {
          items: {
            type: 'string',
          },
          type: 'array',
        },
        {
          type: 'null',
        },
      ],
      default: null,
      title: 'Default User Scopes',
    },
  },
  required: ['name', 'proxy', 'token_url', 'authorization_url'],
  title: 'OAuth2AuthScheme',
  type: 'object',
}

const authToSchema = (auth: string) => {
  switch (auth) {
    case 'BASIC':
      return BasicAuthScheme

    case 'NO_AUTH':
      return NoAuthScheme

    case 'API_KEY':
      return ApiKeyAuthScheme

    case 'OAUTH1':
      return OAuth1AuthScheme

    case 'OAUTH2':
      return OAuth2AuthScheme

    default:
      return NoAuthScheme
  }
}

const OAuthSchemes = ['OAUTH1', 'OAUTH2']

const AuthSchemes = [
  'BASIC',
  'BEARER_TOKEN',
  'GOOGLE_SERVICE_ACCOUNT',
  'NO_AUTH',
  'API_KEY',
  'OAUTH1',
  'OAUTH2',
  'BASIC_WITH_JWT',
]

function loadYaml(content: string) {
  try {
    const parsed = parse(content)
    return {
      data: parsed,
      error: null,
    }
  } catch (err) {
    if (err instanceof YAMLParseError) {
      return {
        data: null,
        error: err.message,
      }
    }

    throw err
  }
}

export interface ValidationResponse {
  errors: string[] | null
}

export function validateIntegrationYaml(content: string): ValidationResponse {
  const { data, error } = loadYaml(content)
  if (error) {
    return {
      errors: [error],
    }
  }

  const validator = new Validator()
  let errors: string[] = validator
    .validate(data, IntegrationSchema)
    .errors.map((error) => {
      return error.message
    })

  if (errors.length > 0) return { errors: errors }
  errors = data.auth_schemes // @ts-expect-error - this is okay for now
    .map((scheme, i) => {
      if (!Object.keys(scheme).includes('auth_mode')) {
        return [`auth_schemes.${i} - requires property "auth_mode"`]
      }

      if (
        OAuthSchemes.includes(scheme.auth_mode) &&
        (data.get_current_user_endpoint === null ||
          data.get_current_user_endpoint === undefined)
      ) {
        return [
          `'get_current_user_endpoint' is required since ${scheme.name} supports ${scheme.auth_mode} auth scheme"`,
        ]
      }

      if (!AuthSchemes.includes(scheme.auth_mode)) {
        let error = `Invalid auth mode: ${scheme.auth_mode}`
        const matches = getCloseMatches(
          scheme.auth_mode.toUpperCase().replace('-', '_'),
          AuthSchemes,
        )
        if (matches.length > 0) {
          error = `${error}, did you mean: ${matches.join(', ')}`
        }
        return [error]
      }

      try {
        return validator
          .validate(scheme, authToSchema(scheme.auth_mode))
          .errors.map((error) => {
            return `auth_schemes.${i} - ${error.message}`
          })
      } catch (error) {
        return [
          `auth_schemes.${i} - Error validating scheme: ${error instanceof Error ? error.message : String(error)}`,
        ]
      }
    }) // @ts-expect-error - this is okay for now
    .reduce((a, b) => a.concat(b))

  if (errors.length > 0) return { errors: errors }

  return { errors: null }
}

export function validateSpecificationYaml(content: string): ValidationResponse {
  const { data, error } = loadYaml(content)

  if (error) {
    return {
      errors: [error],
    }
  }

  const errors = []

  if (data.info) {
    if (!data.info.title) {
      errors.push('requires property "info.title"')
    }

    if (!data.info.version) {
      errors.push('requires property "info.version"')
    }
  } else {
    errors.push('requires property "info"')
  }

  if (errors.length > 0) {
    return {
      errors: errors,
    }
  }

  return {
    errors: null,
  }
}
